import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { PapiStudioSellerStatsInit } from '@wix/da-papi-types';
import { sanitizeHref } from '@wix/da-url';
import ReportsSection from '../ReportsSection';
import ReportsTable from '../ReportsTable';
import { ProductType } from '../../products/types';
import ProductTypeIcon from '../SoldProducts/ProductTypeIcon';
import { getDeviationPrice } from '@wix/da-shared-react/pkg/utils/deviations';

import s from './SoldSubscriptionsSection.scss';

export interface Props {
  className?: string;
  data?: PapiStudioSellerStatsInit['soldSubscriptions'];
}

const SoldSubscriptionsSection: FC<Props> = ({ data, className }) => {
  const { t } = useTranslation();
  const columns = useMemo(() => {
    return [
      {
        key: 'tier',
        label: t('studio.reports.section.subscriptionSales.table.header.tier'),
      },
      {
        key: 'tierPrice',
        label: t('studio.reports.section.subscriptionSales.table.header.tierPrice'),
      },
      {
        key: 'numSubscribers',
        label: t('studio.reports.section.subscriptionSales.table.header.numSubscribers'),
      },
      {
        key: 'salesVolume',
        label: t('studio.reports.section.subscriptionSales.table.header.salesVolume'),
      },
    ];
  }, [t]);

  if (!data) {
    return null;
  }

  return (
    <ReportsSection
      reportType="soldSubscriptions"
      className={className}
      title={t('studio.reports.section.soldSubscriptions.title')}
      description={t('studio.reports.section.soldSubscriptions.description', {
        postProcess: ['reactPostprocessor'],
        learnMoreLink: (
          <a
            className="text-link-level-2-tertiary"
            href={sanitizeHref(
              t('studio.reports.section.soldSubscriptions.description.learnMoreLink.url')
            )}
          >
            {t('studio.reports.section.soldSubscriptions.description.learnMoreLink.text')}
          </a>
        ),
      })}
      data={data}
      dataFilter={dataToFilter =>
        /* do not shows rows that have no sales */
        dataToFilter?.filter(item => (item.salesVolume || 0) > 0)
      }
      renderTable={(filteredData: any[]) => (
        <ReportsTable
          columns={columns}
          data={filteredData}
          renderRow={({ deviation, numSubscribers, uniqueCustomers, salesVolume }: any) => (
            <>
              <td>
                <div className={s['tier']}>
                  <ProductTypeIcon productType={ProductType.Tier} size={40} />
                  <div className={s['tier-info']}>
                    <div className={s['title']}>{deviation?.title}</div>
                    <div className={s['subscribers']}>
                      {t('studio.reports.section.soldSubscriptions.table.activeSubscriberCount', {
                        count: numSubscribers,
                      })}
                    </div>
                  </div>
                </div>
              </td>
              <td>{deviation && formatDollars(getDeviationPrice(deviation).dollarsPrice)}</td>
              <td>{uniqueCustomers}</td>
              <td>{formatDollars(salesVolume)}</td>
            </>
          )}
          renderFooter={(totals: any) => {
            return (
              <tr>
                <td>{t('studio.reports.section.common.table.footer.total')}</td>
                <td>---</td>
                <td>{totals?.uniqueCustomers}</td>
                <td>{formatDollars(totals?.salesVolume)}</td>
              </tr>
            );
          }}
        />
      )}
    />
  );
};

export default SoldSubscriptionsSection;
